// noinspection TypeScriptValidateTypes

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ReactNode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClient, QueryClientProvider } from 'react-query';
import List from './js/List/List';
import Service from './js/Service/Service';
import Api from './js/api';
import defaultStyle from './stylesheet.css?style-provider';

const queryClient = new QueryClient();

// Suppress that stupid video.js warning about it not being in the DOM
// No shit, it's in the shadow dom...
const defaultWarn = window.console.warn;
window.console.warn = function (...args) {
  if (args[2] !== 'The element supplied is not included in the DOM') {
    defaultWarn(...args);
  }
};

const EmotionStyleRedirect = ({ children, container }: { children: ReactNode; container: ShadowRoot }) => {
  const cache = useMemo(
    () =>
      createCache({
        key: 'shadow-cache',
        container: container as unknown as HTMLElement,
      }),
    [container],
  );

  return <CacheProvider value={cache}>{children}</CacheProvider>;
};

if (!document.querySelector('[data-funeral-stream-font]')) {
  const externalStyles = [
    'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&display=swap',
    'https://unpkg.com/videojs-font@3.2.0/css/videojs-icons.css',
  ];

  externalStyles.forEach((url) => {
    let linkNode = document.createElement('link');
    linkNode.setAttribute('data-funeral-stream-font', '');
    linkNode.type = 'text/css';
    linkNode.rel = 'stylesheet';
    linkNode.href = url;
    document.head.appendChild(linkNode);
  });
}

const EmbedReact = ({
  list,
  listStyle,
  service,
  password,
  search,
}: {
  list: string | null;
  listStyle: string | null;
  service: string | null;
  password: string | undefined;
  search: string;
}) => {
  if (service) {
    return (
      <QueryClientProvider client={queryClient}>
        <Service serviceHash={service} organizationHash={list} password={password} />
      </QueryClientProvider>
    );
  }
  if (list) {
    return (
      <QueryClientProvider client={queryClient}>
        <List listCode={list} search={search} listStyle={listStyle} />
      </QueryClientProvider>
    );
  }

  return <div className="test-class">Funeral Stream embed is not configured correctly</div>;
};

function Message() {
  //get the message from the url
  const message = new URLSearchParams(window.location.search).get('message');

  if (!message) return null;

  return <div className="outer-component-message">{message}</div>;
}

function ErrorFallback({ host, list, funeral }) {
  const legacyDomain = (host ?? 'https://app.funeral.stream').replace(/app.|api.|assets./, 'legacy.');
  let redirectLocation;
  if (funeral) {
    redirectLocation = legacyDomain + '/funeral/' + funeral;
  } else {
    redirectLocation = legacyDomain + '/list/' + list;
  }

  return (
    <div className="error-modal-outer-container">
      <div className="error-modal-main">
        <div className="error-modal-header">Page could not fully load</div>
        <div className="error-modal-message">
          Please try refreshing this page.. If the problem persists, your device may require an update to make use of the full features on this page.
          You can view a downgraded experience below.
        </div>
        <div className="error-modal-button-container">
          <a className="accent-button" href={redirectLocation}>
            View downgraded experience
          </a>

          <button
            className="plain-button"
            onClick={() => {
              window.location.reload();
            }}
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

class FuneralStreamReact extends HTMLElement {
  async connectedCallback() {
    const mountPoint = document.createElement('div');
    const shadow = this.attachShadow({ mode: 'open' });

    let searchParams = new URLSearchParams(window.location.search);

    const vjslink = document.createElement('link');
    vjslink.type = 'text/css';
    vjslink.rel = 'stylesheet';
    vjslink.href = 'https://vjs.zencdn.net/8.0.4/video-js.css';
    shadow.appendChild(vjslink);

    if (!this.hasAttribute('override-css')) {
      defaultStyle(shadow).mount();
    }

    if (this.hasAttribute('css')) {
      const link = document.createElement('link');
      link.type = 'text/css';
      link.rel = 'stylesheet';
      link.href = this.getAttribute('css') as string;
      shadow.appendChild(link);
    }

    if (this.hasAttribute('host')) {
      Api.setHost(this.getAttribute('host') as string);
    } else {
      Api.setHost('https://api.funeral.stream');
    }

    const code = searchParams.get('funeral') ?? searchParams.get('code');

    shadow.appendChild(mountPoint);

    const events = ['keydown', 'keyup', 'keypress', 'input'];
    const stopPropagation = (e) => e.stopPropagation();
    events.forEach((event) => mountPoint.addEventListener(event, stopPropagation));

    const root = createRoot(mountPoint);

    root.render(
      <ErrorBoundary
        FallbackComponent={() => (
          <ErrorFallback host={this.getAttribute('host') ?? 'https://api.funeral.stream'} funeral={code} list={this.getAttribute('list')} />
        )}
      >
        <EmotionStyleRedirect container={shadow}>
          <Message />
          <EmbedReact
            list={this.getAttribute('list')}
            listStyle={this.getAttribute('list-style')}
            service={code}
            password={this.getAttribute('password') ?? searchParams.get('password') ?? ''}
            search={searchParams.get('search') ?? ''}
          />
        </EmotionStyleRedirect>
      </ErrorBoundary>,
    );
  }
}

if (customElements.get('funeral-stream') === undefined) {
  customElements.define('funeral-stream', FuneralStreamReact);
}
